import { Text } from "components/common/Text";
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export type InfoRow = { label: string; value?: React.ReactNode };

export type Anchor = {
  /** ratio 0..1 in container (x from left, y from top) */
  x: number;
  y: number;
};

export const InfoCard: React.FC<{
  title: string;
  titleRight?: React.ReactNode;
  headerBg: string;
  rows: InfoRow[];
  /** When provided, clicking outside or pressing ESC will call this */
  onClose?: () => void;
  /** If provided, card will be positioned near this anchor inside containerRef */
  anchor?: Anchor;
  /** The container that defines coordinate space for anchor (required when anchor is used) */
  containerRef?: React.RefObject<HTMLElement | null>;
  /** Preferred first placement around anchor */
  prefer?: "above" | "below" | "left" | "right";
  /** If true, show a dim backdrop that also captures outside clicks */
  withBackdrop?: boolean;
}> = ({
  title,
  titleRight,
  headerBg,
  rows,
  onClose,
  anchor,
  containerRef,
  prefer = "above",
  withBackdrop = true,
}) => {
  const [cardSize, setCardSize] = useState({ width: 0, height: 0 });
  const cardRef = useRef<HTMLDivElement | null>(null);
  const fallbackWidth = useMemo(() => {
    if (typeof window === "undefined") return 200;
    return Math.min(window.innerWidth * 0.32, 220);
  }, []);
  const fallbackHeight = 160;

  useLayoutEffect(() => {
    if (!cardRef.current) return;
    const measure = () => {
      if (!cardRef.current) return;
      const rect = cardRef.current.getBoundingClientRect();
      setCardSize({ width: rect.width, height: rect.height });
    };

    measure();

    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measure);
      observer.observe(cardRef.current);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", measure);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", measure);
      }
    };
  }, [rows, title, titleRight]);

  // Close on ESC
  useEffect(() => {
    if (!onClose) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const positioned = useMemo(() => {
    if (!anchor || !containerRef?.current) return null;

    const host = containerRef.current as HTMLElement;
    const rect = host.getBoundingClientRect();
    const cardWidth = cardSize.width || fallbackWidth;
    const cardHeight = cardSize.height || fallbackHeight;
    const viewport =
      typeof window !== "undefined"
        ? {
            left: 8,
            top: 8,
            right: window.innerWidth - 8,
            bottom: window.innerHeight - 8,
          }
        : {
            left: rect.left,
            top: rect.top,
            right: rect.right,
            bottom: rect.bottom,
          };
    const clampBounds = {
      minLeft: Math.max(rect.left + 8, viewport.left),
      maxLeft: Math.min(rect.right - 8, viewport.right) - cardWidth,
      minTop: Math.max(rect.top + 8, viewport.top),
      maxTop: Math.min(rect.bottom - 8, viewport.bottom) - cardHeight,
    };

    clampBounds.maxLeft = Math.max(clampBounds.minLeft, clampBounds.maxLeft);
    clampBounds.maxTop = Math.max(clampBounds.minTop, clampBounds.maxTop);

    const pxX = rect.left + anchor.x * rect.width;
    const pxY = rect.top + anchor.y * rect.height;

    type PL = "above" | "below" | "left" | "right";
    const tries: PL[] = [prefer, "right", "left", "below", "above"].filter(
      (v, i, a) => a.indexOf(v) === i
    ) as PL[];

    const fits = (pl: PL) => {
      let left = pxX - cardWidth / 2;
      let top = pxY - cardHeight / 2;
      if (pl === "above") {
        top = pxY - 12 - cardHeight;
      } else if (pl === "below") {
        top = pxY + 12;
      } else if (pl === "right") {
        left = pxX + 12;
      } else if (pl === "left") {
        left = pxX - 12 - cardWidth;
      }

      if (pl === "above" || pl === "below") {
        left = pxX - cardWidth / 2;
      } else {
        top = pxY - cardHeight / 2;
      }

      const clLeft = Math.max(
        clampBounds.minLeft,
        Math.min(clampBounds.maxLeft, left)
      );
      const clTop = Math.max(
        clampBounds.minTop,
        Math.min(clampBounds.maxTop, top)
      );

      const overflow =
        (left < clampBounds.minLeft ? clampBounds.minLeft - left : 0) +
        (left > clampBounds.maxLeft ? left - clampBounds.maxLeft : 0) +
        (top < clampBounds.minTop ? clampBounds.minTop - top : 0) +
        (top > clampBounds.maxTop ? top - clampBounds.maxTop : 0);

      return { left: clLeft, top: clTop, overflow };
    };

    let best = {
      left: clampBounds.minLeft,
      top: clampBounds.minTop,
      overflow: Number.POSITIVE_INFINITY,
    };
    for (const pl of tries) {
      const cand = fits(pl);
      if (cand.overflow === 0) {
        best = cand;
        break;
      }
      if (cand.overflow < best.overflow) best = cand;
    }

    return {
      left: best.left - rect.left,
      top: best.top - rect.top,
    };
  }, [
    anchor,
    containerRef,
    prefer,
    cardSize.width,
    cardSize.height,
    fallbackWidth,
  ]);

  const CardContent = (
    <div
      ref={cardRef}
      role="dialog"
      aria-label={title}
      onClick={(e) => e.stopPropagation()}
      className="relative select-none overflow-hidden rounded-lg bg-white text-black border border-border shadow-2xl shadow-black/25 touch-manipulation"
      style={{
        width: "min(32vw, 320px)",
        maxWidth: "90vw",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <div
        className="flex items-center justify-between px-[1.2vw] py-[1vw] font-bold text-white shadow-lg"
        style={{ background: headerBg }}
      >
        <Text
          variant="soccerInfoCardTitle"
          className="max-w-[62%] truncate text-outline"
        >
          {title}
        </Text>

        <Text variant="soccerInfoCardTitle" className="text-outline">
          {titleRight}
        </Text>
      </div>
      <div className="w-full px-[1.2vw] py-[1vw] leading-snug">
        {rows.map((r, i) => (
          <div
            key={i}
            className="flex w-full items-center justify-between gap-2 last:mb-0"
          >
            <Text variant="soccerInfoCardDescription" className="text-left">
              {r.label}
            </Text>
            {r.value && (
              <Text variant="soccerInfoCardDescription" className="text-right">
                {r.value}
              </Text>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  // Plain/inline mode (no anchor)
  if (!positioned) {
    return CardContent;
  }

  // Anchored mode inside container
  return (
    <>
      {withBackdrop && (
        <div className="fixed inset-0 z-40 bg-black/10" onClick={onClose} />
      )}
      <div
        className="absolute z-50"
        style={{
          left: positioned.left,
          top: positioned.top,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {CardContent}
      </div>
    </>
  );
};
